// Titanium Wares - cart lines in the ChopChop USA format (owner 23/10/2026), drawer + /cart page.
// snippets/cart-products.liquid (tw-cc) prints one card per product; this file drives the card's own controls:
//   [data-tw-step]          the card's ONE stepper. + / − act on the line of the last unit by clicking that line's own
//                           (hidden) Horizon stepper / trash, so assets/st-gift-guard.js's fast path still sends the product
//                           and its gifts in one request (or the theme's own path for a product without gifts).
//   [data-tw-group-remove]  the card's trash: every line of the product and the gifts hanging on them, one /cart/update.js.
//   select[data-tw-unit]    one select per unit: moves ONE unit to the chosen variant (/cart/update.js keyed by variant id,
//                           like assets/st-cart-variant.js); the gift guard re-checks the gifts on the cart:update event.
//   details.tw-cc-save      the You save band folds again after every cart change.
// Loaded on every page from snippets/header-actions.liquid (the drawer is re-rendered, so the listeners are delegated).
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

if (!window.__twCartCc) {
  window.__twCartCc = true;

  let busy = false;

  // "You save" (snippets/tw-cart-save.liquid) folds again after every cart change, like ChopChop's: the re-rendered drawer
  // keeps an opened <details> open otherwise
  const fold = () => {
    for (const details of document.querySelectorAll('details.tw-cc-save[open]')) details.open = false;
  };
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    setTimeout(fold, 0);
    setTimeout(fold, 400);
  });

  const rowOf = (el, key) => {
    const host = el.closest('cart-items-component');
    if (!host || !key) return null;
    return [...host.querySelectorAll('tr[data-key]')].find((row) => row.dataset.key === key) || null;
  };

  const update = async (el, updates) => {
    const host = el.closest('cart-items-component');
    const sectionId = host instanceof HTMLElement ? host.dataset.sectionId || '' : '';
    host?.classList.add('cart-items-disabled');
    try {
      const body = JSON.stringify({
        updates,
        ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {}),
      });
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Cart update failed');
      el.dispatchEvent(
        new CartUpdateEvent(data, sectionId || 'tw-cart-cc', {
          source: 'tw-cart-cc',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } finally {
      host?.classList.remove('cart-items-disabled');
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const step = target.closest('[data-tw-step]');
    if (step instanceof HTMLButtonElement) {
      event.preventDefault();
      if (step.disabled) return;
      const row = rowOf(step, step.dataset.key || '');
      if (!row) return;
      const input = row.querySelector('input[name="updates[]"], input[type="number"]');
      const qty = Number(input?.value || 0) || 0;
      const plus = step.dataset.twStep === 'plus';
      const real = plus
        ? row.querySelector('button[name="plus"]')
        : qty <= 1
          ? row.querySelector('.cart-items__remove')
          : row.querySelector('button[name="minus"]');
      if (!(real instanceof HTMLButtonElement) || real.disabled) return;
      const n = step.parentElement?.querySelector('.tw-cc__qty-n');
      if (n) n.textContent = String(Math.max(0, (Number(n.textContent) || 0) + (plus ? 1 : -1)));
      step.classList.add('tw-busy');
      real.click();
      return;
    }

    const remove = target.closest('[data-tw-group-remove]');
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      if (remove.getAttribute('aria-busy') === 'true') return;
      const keys = (remove.dataset.keys || '').split(',').filter(Boolean);
      if (!keys.length) return;
      const host = remove.closest('cart-items-component');
      const updates = {};
      for (const key of keys) updates[key] = 0;
      for (const row of host?.querySelectorAll('tr[data-parent-key]') || []) {
        if (row instanceof HTMLElement && keys.includes(row.dataset.parentKey || '')) updates[row.dataset.key || ''] = 0;
      }
      delete updates[''];
      remove.setAttribute('aria-busy', 'true');
      remove.closest('tr')?.classList.add('tw-removing');
      update(remove, updates)
        .catch((error) => {
          console.warn('[tw-cart-cc] remove', error);
          remove.closest('tr')?.classList.remove('tw-removing');
        })
        .finally(() => remove.removeAttribute('aria-busy'));
    }
  });

  document.addEventListener('change', async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('select[data-tw-unit]')) return;
    const from = select.dataset.from || '';
    const to = select.value;
    if (!from || !to || from === to) return;
    if (busy) {
      select.value = from;
      return;
    }
    const fromOption = [...select.options].find((option) => option.value === from);
    const fromTotal = Number(fromOption?.dataset.cartQty || select.dataset.lineQty || 1) || 1;
    const toTotal = Number(select.selectedOptions[0]?.dataset.cartQty || 0) || 0;
    const label = select.closest('.tw-cc__unit');
    busy = true;
    select.disabled = true;
    label?.classList.add('tw-busy');
    try {
      await update(select, { [from]: Math.max(0, fromTotal - 1), [to]: toTotal + 1 });
    } catch (error) {
      select.value = from;
      console.warn('[tw-cart-cc] unit', error);
    } finally {
      busy = false;
      select.disabled = false;
      label?.classList.remove('tw-busy');
    }
  });
}
